import { MouseEvent, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { ButtonsTabs } from '@/components/ButtonsTabs';
import { Icon } from '@/components/Icon';
import { useMediasInProgress } from '@/hooks/useMediasInProgress';
import { useLibraryTabs } from '@/modules/library/hooks/useLibraryTabs';
import { useMediasInHistory } from '@/modules/library/hooks/useMediasInHistory';
import { BlockMediaInProgress } from '@/modules/library/presentation/BlockMediaInProgress';
import { LibraryLoader } from '@/modules/library/presentation/LibraryLoader';
import { useMediasPrograms } from '@/modules/medias/hooks/useMediasPrograms';
import { useNotifications } from '@/modules/notifications/hooks/useNotifications';
import { deleteMediaFromLibrary } from '@/services/firestore/deleteMediaFromLibrary';
import { getCompletionDate } from '@/utils';

const History = () => {
  const { formatMessage } = useIntl();
  const { showNotificationError } = useNotifications();
  const { libraryTabs } = useLibraryTabs();

  const { mediasInHistory, hasError, isLoading: isLoadingHistory } = useMediasInHistory();
  const { mediasInProgress, isLoading: isLoadingInProgress } = useMediasInProgress();
  const { mediasProgramsData: mediasInHistoryData, isLoading: isLoadingMediasInHistoryData } =
    useMediasPrograms(mediasInHistory);
  const { mediasProgramsData: mediasInProgressData, isLoading: isLoadingInProgressInHistoryData } =
    useMediasPrograms(mediasInProgress);

  useEffect(() => {
    if (hasError) {
      showNotificationError('library.history.notifications.error');
    }
  }, [hasError, showNotificationError]);

  const handleHideEpisode = useCallback(
    async (UUID: string, e: MouseEvent) => {
      e.stopPropagation();

      try {
        await deleteMediaFromLibrary(UUID);
      } catch (e) {
        showNotificationError('library.history.deleteEpisode.notifications.error');
      }
    },
    [showNotificationError]
  );

  return (
    <div className="content">
      <h1 className="title-h1 mx-5 py-10">{formatMessage({ id: 'library.title' })}</h1>
      <ButtonsTabs pageAssociated="library" buttons={libraryTabs} activeButton="history" />
      {isLoadingMediasInHistoryData ||
      isLoadingInProgressInHistoryData ||
      isLoadingHistory ||
      isLoadingInProgress ? (
        <LibraryLoader />
      ) : (
        <>
          {!mediasInHistory?.length ? (
            <div className="grid-no-result">
              <div>
                <h2 className="title-h3">
                  {formatMessage({ id: 'library.emptyView.history.title' })}
                </h2>
                <p className="text-sm text-brand-neutral-500">
                  {formatMessage({ id: 'library.emptyView.history.description' })}
                </p>
              </div>
            </div>
          ) : (
            <>
              <h2 className="title-h2 mx-5 mb-0">
                {formatMessage({ id: 'library.history.resume.title' })}
              </h2>
              <div className="grid-library">
                {mediasInProgressData.map(({ media, program, history }) => (
                  <BlockMediaInProgress
                    key={media.UUID}
                    media={media}
                    lastTimeInMedia={history?.lastTimeInMedia}
                    program={program}
                    handleHideEpisode={handleHideEpisode}
                  />
                ))}
              </div>
              <h2 className="title-h2 mx-5 mb-0">
                {formatMessage({ id: 'library.history.history.title' })}
              </h2>
              <div className="grid-library">
                {mediasInHistoryData.map(({ media, program, history }) => (
                  <div key={media.UUID}>
                    <div className="flex my-2 items-center text-sm lg:text-base">
                      <Icon name="clock" className="text-2xl mr-1 lg:text-2xl" />
                      <p>{getCompletionDate(history.lastCompletionDate)}</p>
                    </div>

                    <BlockMediaInProgress
                      media={media}
                      lastTimeInMedia={history.lastTimeInMedia}
                      key={media.UUID}
                      program={program}
                      handleHideEpisode={handleHideEpisode}
                      isListened
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default History;
